import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  socialTeamListUsers,
  socialTeamListLog,
  socialTeamCopySetup,
} from "@/lib/social-team.functions";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Lock, Users, Copy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/social/team-setup")({
  component: TeamSetupPage,
  ssr: false,
});

function TeamSetupPage() {
  const { isAdmin, loading } = useAdminAuth();
  const qc = useQueryClient();
  const listUsers = useServerFn(socialTeamListUsers);
  const listLog = useServerFn(socialTeamListLog);
  const copy = useServerFn(socialTeamCopySetup);

  const [target, setTarget] = useState("");
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [includeEvergreen, setIncludeEvergreen] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const usersQ = useQuery({
    queryKey: ["social-team-users"],
    queryFn: () => listUsers(),
    enabled: isAdmin,
  });
  const logQ = useQuery({
    queryKey: ["social-team-log"],
    queryFn: () => listLog(),
    enabled: isAdmin,
  });

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border bg-card p-10 text-center">
        <Lock className="h-6 w-6 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Admin only</h2>
        <p className="max-w-sm text-sm text-muted-foreground">
          Only admins can copy company posts into a team member&apos;s setup.
        </p>
      </div>
    );
  }

  const run = async () => {
    if (!target) {
      toast.error("Pick a team member to copy into");
      return;
    }
    if (!includeDrafts && !includeEvergreen) {
      toast.error("Select at least one type to copy");
      return;
    }
    setBusy(true);
    try {
      const res = await copy({
        data: {
          targetUserId: target,
          includeDrafts,
          includeEvergreen,
          note: note.trim() || null,
        },
      });
      toast.success(
        `Copied ${res.copiedDrafts} draft(s) and ${res.copiedEvergreen} evergreen post(s).`,
      );
      setNote("");
      qc.invalidateQueries({ queryKey: ["social-team-log"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Copy failed");
    } finally {
      setBusy(false);
    }
  };

  const users = usersQ.data?.users ?? [];
  const userName = (id: string) => {
    const u = users.find((x: any) => x.id === id);
    return u?.display_name || u?.email || id.slice(0, 8);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Team setup</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Fast-onboard a team member by copying the company&apos;s drafts and evergreen library into
          their account. Copied evergreen posts start <strong>paused</strong> with no accounts
          attached so they can review and connect their own accounts first.
        </p>

        <div className="space-y-4 rounded-lg border border-border bg-card p-4">
          <div>
            <Label htmlFor="target">Copy into team member</Label>
            <select
              id="target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Select a user…</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.display_name || u.email || u.id}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Copy AI drafts &amp; saved drafts</span>
            <Switch checked={includeDrafts} onCheckedChange={setIncludeDrafts} />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Copy evergreen library</span>
            <Switch checked={includeEvergreen} onCheckedChange={setIncludeEvergreen} />
          </label>

          <div>
            <Label htmlFor="note">Note (optional)</Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Onboarding starter pack"
              maxLength={500}
            />
          </div>

          <Button onClick={run} disabled={busy}>
            <Copy className="mr-2 h-4 w-4" />
            {busy ? "Copying…" : "Copy setup"}
          </Button>
        </div>
      </div>

      <aside className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Recent copies</h3>
        {logQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {logQ.data && (logQ.data.log ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">No copies yet.</p>
        )}
        <ul className="space-y-2">
          {(logQ.data?.log ?? []).map((row: any) => (
            <li key={row.id} className="rounded-md border border-border p-2 text-xs">
              <p className="font-medium">{userName(row.target_user_id)}</p>
              <p className="text-muted-foreground">
                {row.copied_drafts} drafts · {row.copied_evergreen} evergreen
              </p>
              {row.note && <p className="mt-0.5 italic text-muted-foreground">“{row.note}”</p>}
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {new Date(row.created_at).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
